import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, AlertTriangle } from "lucide-react";

const FILTERS = [["soon", "Expiring ≤30 days"], ["expired", "Expired"], ["all", "All batches"]];

export function daysLabel(b) {
  if (b.days_left === null || b.days_left === undefined) return "no expiry";
  if (b.days_left < 0) return `expired ${-b.days_left}d ago`;
  if (b.days_left === 0) return "expires today";
  return `${b.days_left}d left`;
}

export function StatusPill({ b }) {
  const cls = b.status === "expired" ? "bg-rose-600 text-white" : b.status === "expiring" ? "bg-amber-500 text-white" : "bg-zinc-100 text-zinc-600";
  return <span data-testid={`batch-status-${b.id}`} className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 ${cls}`}>{daysLabel(b)}</span>;
}

export function BatchTable({ rows, showItem = true }) {
  return (
    <div className="bg-white border border-zinc-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50"><tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500">{[showItem ? "Item" : null, "Batch", "Godown", "Expiry", "Qty", "Status"].filter(Boolean).map(h => <th key={h} className={`px-3 py-2 font-semibold text-left ${h === "Qty" ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map(b => <tr key={b.id} data-testid={`batch-row-${b.id}`} className={`border-t border-zinc-100 ${b.status === "expired" ? "bg-rose-50/60" : b.status === "expiring" ? "bg-amber-50/60" : ""}`}>
            {showItem ? <td className="px-3 py-2 font-medium">{b.item_name}</td> : null}
            <td className="px-3 py-2 tabular">{b.batch_no || "—"}</td>
            <td className="px-3 py-2 text-xs">{b.godown_name || "—"}</td>
            <td className="px-3 py-2 tabular text-xs">{b.expiry || "—"}</td>
            <td className="px-3 py-2 text-right tabular font-semibold">{b.qty} {b.unit}</td>
            <td className="px-3 py-2"><StatusPill b={b} /></td>
          </tr>)}
          {!rows.length && <tr><td colSpan={6} className="px-3 py-10 text-center text-zinc-500 text-sm" data-testid="batches-empty">No batches here.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function ExpiryPanel({ qs }) {
  const [filter, setFilter] = useState("soon");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/inventory/batches${qs}`).then(r => setRows(r.data)); }, [qs]);
  const shown = rows.filter(b => filter === "all" ? true : filter === "expired" ? b.status === "expired" : b.status !== "ok");
  return (
    <div className="space-y-3" data-testid="expiry-panel">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, l]) => <button key={k} data-testid={`expiry-filter-${k}`} onClick={() => setFilter(k)} className={`h-9 text-xs font-semibold px-3 border flex items-center gap-1 ${filter === k ? "bg-zinc-900 border-zinc-900 text-white" : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>{k === "expired" ? <AlertTriangle size={13} /> : <Clock size={13} />} {l}</button>)}
        <span className="ml-auto text-xs text-zinc-500" data-testid="expiry-count">{shown.length} batch(es)</span>
      </div>
      <BatchTable rows={shown} />
      <p className="text-[11px] text-zinc-500">Enable “Track batches & expiry” on an item, then record batch no. and expiry when adding stock or purchases. Bills consume the earliest-expiring batch first.</p>
    </div>
  );
}

export function BatchesDialog({ item, onClose, qs }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (item) api.get(`/inventory/batches${qs}${qs ? "&" : "?"}item_id=${item.id}`).then(r => setRows(r.data)); }, [item, qs]);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="batches-dialog">
        <DialogHeader><DialogTitle className="font-heading tracking-tight text-xl">Batches · {item?.name}</DialogTitle><DialogDescription>Current stock {item?.total_stock} {item?.unit}. Earliest expiry is sold first.</DialogDescription></DialogHeader>
        <BatchTable rows={rows.filter(b => b.qty > 0)} showItem={false} />
      </DialogContent>
    </Dialog>
  );
}
